import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { ACL_MATRIX } from 'common/permissions';
import { TABLE_PAGE_SIZE_OPTIONS } from 'constants/fulfillment';
import { isUserAuthorized } from 'utils/helpers';

import BRButton from 'components/BRButton/BRButton';
import BRTable from 'components/BRTable/BRTable';
import LoadingWrapper from 'components/LoadingWrapper/LoadingWrapper';

import { ReactComponent as EyeIcon } from 'assets/bosta-icons/Eye.svg';

import './CreditsRecharge.less';

const CreditsRecharge = ({
  isLoading,
  setIsLoading,
  isviewReceiptDisabled,
  handleViewReceipt,
  handleReceiptRejectRequest,
  handleApproveRechargeRequest,
  handleGetRequests,
  ...props
}) => {
  const intl = useIntl();
  const [requestsCount, setRequestsCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');

  const refreshTable = useRef();

  const handleRejectRequest = async (requestId) => {
    const onSuccess = () => refreshTable.current();
    await handleReceiptRejectRequest(requestId, onSuccess);
  };

  const handleApproveRequest = async (requestId) => {
    const onSuccess = () => refreshTable.current();
    await handleApproveRechargeRequest(requestId, onSuccess);
  };

  const handleFetchRequests = async ({ page, limit, searchInputText }) => {
    setSearchValue(searchInputText);

    const payload = {
      rechargeType: 'bosta credit',
      ...(searchInputText && { businessId: searchInputText })
    };

    const data = await handleGetRequests(payload);

    setRequestsCount(data.length);
    return {
      list: data,
      total: data.length
    };
  };

  const columns = [
    {
      dataIndex: 'transaction_id',
      title: intl.formatMessage({
        id: 'recharge_requests.bosta_credits.table.transaction_id'
      })
    },

    {
      dataIndex: 'business_id',
      title: intl.formatMessage({
        id: 'recharge_requests.bosta_credits.table.business_id'
      })
    },
    {
      dataIndex: 'business_name',
      title: intl.formatMessage({
        id: 'recharge_requests.bosta_credits.table.business_name'
      })
    },
    {
      dataIndex: 'payment_type',
      title: intl.formatMessage({
        id: 'recharge_requests.bosta_credits.table.payment_method'
      })
    },
    {
      dataIndex: 'amount',
      title: intl.formatMessage({
        id: 'recharge_requests.bosta_credits.table.amount'
      })
    },

    ...(isUserAuthorized([], [ACL_MATRIX.RECHARGE_GET_ATTACHMENT_LINK])
      ? [
          {
            title: intl.formatMessage({
              id: 'recharge_requests.wallet.table.receipt'
            }),
            render: (record) => {
              return (
                <>
                  <BRButton
                    type={'link-color'}
                    prefixIcon={<EyeIcon />}
                    disabled={isviewReceiptDisabled}
                    className={'button-sm'}
                    label={intl.formatMessage({
                      id: 'recharge_requests.bosta_credits.table.view_receipt'
                    })}
                    onClick={() => handleViewReceipt(record.id)}
                  />
                </>
              );
            }
          }
        ]
      : []),
    {
      title: intl.formatMessage({
        id: 'recharge_requests.bosta_credits.table.action'
      }),
      render: (record) => {
        return (
          <div className="br-credits-recharge-table__actions">
            {isUserAuthorized([], [ACL_MATRIX.RECHARGE_APPROVE_REQUEST]) && (
              <BRButton
                type="basic"
                className={'button-sm'}
                label={intl.formatMessage({ id: 'common.approve' })}
                onClick={() => handleApproveRequest(record.id)}
              />
            )}

            {isUserAuthorized([], [ACL_MATRIX.RECHARGE_REJECT_REQUEST]) && (
              <BRButton
                type="basic"
                className={'button-sm'}
                label={intl.formatMessage({ id: 'common.reject' })}
                onClick={() => handleRejectRequest(record.id)}
              />
            )}
          </div>
        );
      }
    }
  ];

  const acceptMethods = (refreshMethod) => {
    refreshTable.current = () => refreshMethod({ pageNumber: 1 });
  };

  return (
    <div className="br-credits-recharge">
      <LoadingWrapper loading={isLoading}>
        <BRTable
          className="br-credits-recharge-table"
          title={intl.formatMessage(
            {
              id: 'recharge_requests.bosta_credits.table.title'
            },
            {
              count: requestsCount
            }
          )}
          columns={columns}
          // isLoading={isLoading}
          listFunction={handleFetchRequests}
          shareMethods={acceptMethods}
          searchPlaceholder={intl.formatMessage({
            id: 'recharge_requests.bosta_credits.table.search_placeholder'
          })}
          showFilter={false}
          showSearch
          pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
          emptySearchContent={{
            title: intl.formatMessage({ id: 'common.no_results_found' }),
            hideAction: !searchValue
          }}
        />
      </LoadingWrapper>
    </div>
  );
};

export default CreditsRecharge;
